import type { FValues, Gradients } from ".";
import { GRAVITY_FT_SEC } from "../../constants";
import type { PipeFrictionContext } from "../d";

export function calculateGradientsSlug(
  context: PipeFrictionContext,
  fValues: FValues
): Gradients {
  const {
    fluidPropertiesContext: {
      fluidProperties: {
        gasDensityLBMFT3,
        liquidSuperficialVelocityFTSEC,
        liquidDensityLBMFT3,
        gasLiquidRatioSCFSTB
      },
      fluidFrictionProperties: {
        liquidInterfacialTension,
        liquidVelocityNumber,
        pipeDiameterNumber,
        liquidReynoldsNumber,
        gasVelocityNumber
      }
    },
    VSG,
    epsilonD
  } = context;
  const { F5, F7, F6tick } = fValues;

  // D261: =(1+D211)*(((D174^0.982)+D214)/((1+D216*D176)^2))
  const slipCorrelation =
    (1 + F5) *
    ((gasVelocityNumber ** 0.982 + F6tick) /
      (1 + F7 * liquidVelocityNumber) ** 2);

  // CHEN FRICTION
  // D263: =(1/(-2*LOG10(D305/3.7065-(5.0452/D303)*LOG10(((D305^1.1098)/2.8257)+(5.8506/(D303^0.8981))))))^2
  const f1 =
    (1 /
      (-2 *
        Math.log10(
          epsilonD / 3.7065 -
            (5.0452 / liquidReynoldsNumber) *
              Math.log10(
                epsilonD ** 1.1098 / 2.8257 +
                  5.8506 / liquidReynoldsNumber ** 0.8981
              )
        ))) **
    2;
  // D265: =D263*D42*D178^(2/3)

  const f2 = f1 * gasLiquidRatioSCFSTB * pipeDiameterNumber ** (2 / 3);

  // D267: =1+D263*SQRT(D42/50)
  const f3 = 1 + f1 * Math.sqrt(gasLiquidRatioSCFSTB / 50);

  // D269: =D263*(D265/D267)
  const fw = f1 * (f2 / f3);

  // D271: =2*D269*(D176*(D176+D174))/D178
  const frictionGradient =
    (2 *
      fw *
      (liquidVelocityNumber * (liquidVelocityNumber + gasVelocityNumber))) /
    pipeDiameterNumber;

  // D273: =D261/(D63/(D166*D67))^0.25
  const slipVelocity =
    slipCorrelation /
    (liquidDensityLBMFT3 / (liquidInterfacialTension * GRAVITY_FT_SEC)) ** 0.25;

  // D275: =(D273-D307-D150+SQRT((D273-D307-D150)^2+4*D273*D150))/(2*D273)
  const liquidHoldup =
    (slipVelocity -
      VSG -
      liquidSuperficialVelocityFTSEC +
      Math.sqrt(
        (slipVelocity - VSG - liquidSuperficialVelocityFTSEC) ** 2 +
          4 * slipVelocity * liquidSuperficialVelocityFTSEC
      )) /
    (2 * slipVelocity);

  // D277: =(D275*D63+(1-D275)*D62)/(144)
  const staticGradient =
    (liquidHoldup * liquidDensityLBMFT3 +
      (1 - liquidHoldup) * gasDensityLBMFT3) /
    144;

  return {
    slipCorrelation,
    f1,
    f2,
    f3,
    fw,
    frictionGradient,
    slipVelocity,
    liquidHoldup,
    staticGradient
  };
}
